import { QueryClient } from '@tanstack/react-query'
import { notifyListQueryOption } from 'src/hooks/react-query/useCustomQuery.hook'
import { ModalSpace, useModal } from 'src/provider/ModalContextProvider'
import { Bridge } from 'src/utils/bridge'
import EasyLoginSettingsPageView from 'src/pages/EasyLoginSettingPage/view'
import { toggleDataListType } from 'src/pages/NotifySettingPage'
import React, { useEffect, useState } from 'react'
import useCustomCookies from 'src/hooks/useCustomCookies.hook'
import { useToast } from 'src/provider/ToastContextProvider'
import { Loading } from 'src/components/common/Loading'
import RenderIf from 'src/components/common/RenderIf'

export interface EasyLoginSettingsPageProps {
  onToggleChangeHandler: (type: number) => void
  toggleDataList: toggleDataListType[]
}

export const easyLoginSetPageLoader = (queryClient: QueryClient) => async () => {
  const dataQueryValues = [await queryClient.ensureQueryData(notifyListQueryOption())]
  return Promise.allSettled(dataQueryValues)
}

const EasyLoginSettingsPage = () => {
  const { openModal } = useModal()
  const { openToast } = useToast()
  const { c_tel } = useCustomCookies()
  const [isBioAgree, setIsBioAgree] = useState(false)
  const [isBioLock, setIsBioLock] = useState(false)
  const [isSettingLoading, setIsSettingLoading] = useState(true)

  const toggleBiometricAgree = (value: 'Y' | 'N') => {
    if (value === 'Y') {
      Bridge.handleReqBiometricAuth.req()
      Bridge.handleReqBiometricAuth.res((result) => {
        if (result === 'success') {
          Bridge.handleBiometricAgreeState.req(c_tel, value)
          Bridge.handleBiometricAgreeState.res((result) => {
            if (result === 'Y') {
              setIsBioAgree(true)
              openToast({ contents: '생체인증을 사용합니다.' })
            }
          })
        } else if (result === 'error') {
          openModal({
            contents: {
              d: '생체 인증에 실패했어요. 기기 설정을 확인하거나 나중에 다시 시도해 주세요.',
            },
            button: {
              submit: { t: '확인' },
            },
          })
        }
      })
    } else {
      Bridge.handleBiometricAgreeState.req(c_tel, value)
      Bridge.handleBiometricAgreeState.res((result) => {
        if (result !== 'Y') setIsBioAgree(false)
      })
    }
  }

  const toggleDataList: toggleDataListType[] = [
    {
      type: 0,
      text: '생체인증 사용',
      subtext: `생체 인증을 사용하여\n간편 로그인을 진행합니다.`,
      status: isBioAgree,
      modalItems: !isBioAgree
        ? {
            contents: {
              t: '생체 정보 사용',
              d: '간편 비밀번호 로그인 시 생체인증을 사용하시겠습니까?',
            },
            button: {
              submit: {
                t: '확인',
                f: () => toggleBiometricAgree('Y'),
              },
              cancel: { t: '취소' },
            },
          }
        : {
            contents: {
              t: '',
              d: '생체인증 사용을 중지하시겠습니까? 재설정 시 생체인증 사용에 다시 동의해야 합니다.',
            },
            button: {
              submit: {
                t: '사용 중지',
                f: () => toggleBiometricAgree('N'),
              },
              cancel: { t: '취소' },
            },
          },
    },
  ]

  const onToggleChangeHandler = (type: number) => {
    if (isBioLock) {
      openToast({ contents: '해당 기능은 곧 제공될 예정입니다. 조금만 기다려 주세요.' })
      return
    }
    const findModalItems = toggleDataList?.find((w) => w.type === type)?.modalItems
    openModal(findModalItems as ModalSpace.ModalData)
  }

  const handleBioLock = () => {
    setIsBioLock(true)
    setIsSettingLoading(false)
  }

  useEffect(() => {
    Bridge.getBiometricAgreeState.req(c_tel, () => handleBioLock())
    Bridge.getBiometricAgreeState.res(
      (result) => {
        setIsBioAgree(result === 'Y')
        setIsSettingLoading(false)
      },
      () => handleBioLock(),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const props = {
    toggleDataList,
    onToggleChangeHandler,
  }
  return (
    <>
      <RenderIf when={isSettingLoading}>
        <Loading.Default />
      </RenderIf>
      <EasyLoginSettingsPageView {...props} />
    </>
  )
}
export default EasyLoginSettingsPage
